import type {
  DomainRule,
  WorkspaceSecurityPolicy
} from 'src/api/facade/types'

import { mutateMockDb, readMockDb } from '../db/state'
import { createId } from '../utils/ids'

import { appendAuditEvent } from './auditRepo'

export const getWorkspaceSecurityPolicy = (
  workspaceId: number
): WorkspaceSecurityPolicy | undefined =>
  readMockDb().securityPolicies.find((policy) => policy.workspaceId === workspaceId)

export const updateWorkspaceSecurityPolicy = (
  workspaceId: number,
  patch: Partial<Omit<WorkspaceSecurityPolicy, 'workspaceId'>>
): WorkspaceSecurityPolicy | undefined => {
  mutateMockDb((db) => {
    db.securityPolicies = db.securityPolicies.map((policy) =>
      policy.workspaceId === workspaceId
        ? {
            ...policy,
            ...patch
          }
        : policy
    )
  })

  const policy = getWorkspaceSecurityPolicy(workspaceId)
  if (policy) {
    appendAuditEvent({
      scope: 'workspace',
      workspaceId,
      actor: readMockDb().account.profile.email,
      action: 'workspace.security.policy-updated',
      target: 'auth-policy',
      severity: 'high'
    })
  }

  return policy
}

export const listWorkspaceDomainRules = (workspaceId: number): DomainRule[] =>
  readMockDb().domainRules.filter((rule) => rule.workspaceId === workspaceId)

export const createWorkspaceDomainRule = (
  workspaceId: number,
  payload: Omit<DomainRule, 'id' | 'workspaceId' | 'verified'>
): DomainRule => {
  let created: DomainRule | undefined

  mutateMockDb((db) => {
    created = {
      id: createId('domain'),
      workspaceId,
      verified: false,
      ...payload
    }

    db.domainRules.unshift(created)
  })

  const rule = created as DomainRule
  appendAuditEvent({
    scope: 'workspace',
    workspaceId,
    actor: readMockDb().account.profile.email,
    action: 'workspace.domain-rule.created',
    target: rule.domain,
    severity: 'medium'
  })

  return rule
}

export const updateWorkspaceDomainRule = (
  workspaceId: number,
  ruleId: string,
  patch: Partial<Omit<DomainRule, 'id' | 'workspaceId'>>
): DomainRule | undefined => {
  mutateMockDb((db) => {
    db.domainRules = db.domainRules.map((rule) =>
      rule.workspaceId === workspaceId && rule.id === ruleId
        ? {
            ...rule,
            ...patch
          }
        : rule
    )
  })

  const rule = listWorkspaceDomainRules(workspaceId).find((item) => item.id === ruleId)

  if (rule) {
    appendAuditEvent({
      scope: 'workspace',
      workspaceId,
      actor: readMockDb().account.profile.email,
      action: 'workspace.domain-rule.updated',
      target: rule.domain,
      severity: 'medium'
    })
  }

  return rule
}

export const deleteWorkspaceDomainRule = (
  workspaceId: number,
  ruleId: string
): DomainRule[] => {
  let target = ruleId

  mutateMockDb((db) => {
    const rule = db.domainRules.find(
      (item) => item.workspaceId === workspaceId && item.id === ruleId
    )
    target = rule?.domain ?? ruleId
    db.domainRules = db.domainRules.filter(
      (item) => !(item.workspaceId === workspaceId && item.id === ruleId)
    )
  })

  appendAuditEvent({
    scope: 'workspace',
    workspaceId,
    actor: readMockDb().account.profile.email,
    action: 'workspace.domain-rule.deleted',
    target,
    severity: 'medium'
  })

  return listWorkspaceDomainRules(workspaceId)
}
